/**
 * @file VehiclesManager.js
 * @version 1.2.6
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-02-25
 * @modified 2026-10-08
 * @description Manage vehicles (local-only). Add/edit via the rich VehicleModal
 *              (specs, default driver/haulier, compartment layout); supports
 *              archiving and search across registration + specs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './VehiclesManager.css';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import { useSlideOutButton } from './SlideOutButton';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';
import { hasPermission } from '../utils/permissions';
import AccessDenied from './AccessDenied';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import useTableContextMenu from '../hooks/useTableContextMenu';
import VehicleModal from './VehicleModal';
import { useReveal } from '../utils/revealInSearch';
import { formatQuantity, formatTonnes } from '../utils/numberFormatters';

// What Clear Filters resets the search box and archived toggle to.
const CLEARED_FILTERS = { search: '', showArchived: false };

function VehiclesManager({ user }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): searched for.
  const reveal = useReveal('transport-vehicles');
  const [searchTerm, setSearchTerm] = useState(reveal?.term || '');
  const [showArchived, setShowArchived] = useState(false);

  // Add/edit modal — editingVehicle null means "add new".
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);

  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', type: 'warning', confirmText: 'Confirm', onConfirm: null });

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/vehicles?includeArchived=${showArchived}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) setVehicles(data.vehicles);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      setAlertDialog({ isOpen: true, message: 'Failed to load vehicles', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  useSlideOutButton([modalOpen]);

  const filteredVehicles = vehicles.filter(v => matchesAllTerms([
    v.registration,
    v.vehicle_type,
    v.axle_configuration,
    v.default_driver_name,
    v.default_haulier_name,
  ], searchTerm));

  const ctxMenu = useTableContextMenu({
    rows: filteredVehicles,
    getRowId: (v) => v.id
  });

  // Permission gate — placed AFTER all hooks to satisfy react-hooks/rules-of-hooks.
  if (!hasPermission(user, 'transport_planning')) {
    return <AccessDenied permission="Transport Planning" />;
  }

  // ── Modal open/close ──────────────────────────────────────────
  const handleAdd = () => { setEditingVehicle(null); setModalOpen(true); };
  const handleEdit = (vehicle) => { setEditingVehicle(vehicle); setModalOpen(true); };

  const handleSaved = (saved) => {
    setVehicles(prev => {
      const exists = prev.some(v => v.id === saved.id);
      const next = exists
        ? prev.map(v => (v.id === saved.id ? saved : v))
        : [...prev, saved];
      return next.sort((a, b) => a.registration.localeCompare(b.registration));
    });
    setModalOpen(false);
    setEditingVehicle(null);
  };

  // ── Archive ───────────────────────────────────────────────────
  const handleArchive = (vehicle) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Archive Vehicle',
      message: `Archive vehicle "${vehicle.registration}"? It will no longer appear in transport planning or PO dropdowns.`,
      type: 'warning',
      confirmText: 'Archive',
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const res = await fetch(`${API_URL}/api/transport/vehicles/${vehicle.id}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          const data = await res.json();
          if (data.success) {
            if (!showArchived) {
              setVehicles(prev => prev.filter(v => v.id !== vehicle.id));
            } else {
              setVehicles(prev => prev.map(v => v.id === vehicle.id ? { ...v, status: 'ARCHIVED' } : v));
            }
          } else {
            setAlertDialog({ isOpen: true, message: data.error || 'Failed to archive vehicle', type: 'error' });
          }
        } catch (err) {
          setAlertDialog({ isOpen: true, message: 'Failed to archive vehicle', type: 'error' });
        }
      }
    });
  };

  // ── Display helpers ───────────────────────────────────────────
  const fmtWeight = (w) => formatTonnes(w) || '—';
  const fmtType = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '—');

  // Compartment cell. Moveable-door vehicles store an empty compartments array
  // (tonnages vary by configuration), so the count comes from compartment_count
  // and we show the available-slot count rather than a fixed total.
  const renderCompartments = (v) => {
    const count = v.compartment_count != null
      ? v.compartment_count
      : (Array.isArray(v.compartments) ? v.compartments.length : 0);
    if (!count) return '—';
    if (v.has_moveable_doors) {
      const slots = Array.isArray(v.door_positions) ? v.door_positions.length : 0;
      return `${count} ⤢ (${slots} slot${slots === 1 ? '' : 's'})`;
    }
    const total = (Array.isArray(v.compartments) ? v.compartments : [])
      .reduce((s, c) => s + (Number.isFinite(Number(c?.capacity)) ? Number(c.capacity) : 0), 0);
    return `${count} (${formatTonnes(total)})`;
  };

  const buildRowActions = (vehicle) => [
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit vehicle', onClick: () => handleEdit(vehicle) },
    ...(vehicle.status === 'ACTIVE' ? [
      { key: 'archive', icon: 'archive', label: 'Archive', title: 'Archive vehicle', className: 'btn-danger', onClick: () => handleArchive(vehicle) },
    ] : []),
  ];

  // Any header filter applied lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(
    { search: searchTerm, showArchived }, CLEARED_FILTERS, { labels: { showArchived: 'Archived' } }
  );
  const filtersApplied = headerFilters.count > 0;

  return (
    <div className="search-layout vehicles-manager" style={{ zIndex: 2 }}>
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Manage Vehicles</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span>Manage fleet vehicles — specs, default driver/haulier and compartment layout — for transport planning and PO creation.</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={() => { setSearchTerm(CLEARED_FILTERS.search); setShowArchived(CLEARED_FILTERS.showArchived); }}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleAdd}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add Vehicle</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <form className="search-filters" onSubmit={(e) => e.preventDefault()}>
        <div className="filter-row" style={{ gridTemplateColumns: '1fr auto' }}>
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['registration', 'vehicle type', 'axle configuration', 'default driver', 'default haulier']} />
            </label>
            <Input
              type="text"
              placeholder="Search by registration, type, config, driver or haulier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-archived-vehicles"
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <label htmlFor="show-archived-vehicles"></label>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="search-content">
        <div className="table-container">
          {loading ? (
            <LoadingSpinner />
          ) : filteredVehicles.length === 0 ? (
            <div className="no-results">
              <span className="material-symbols-rounded">local_shipping</span>
              <p>{searchTerm ? 'No matching vehicles found' : 'No vehicles available. Click "Add Vehicle" to create one.'}</p>
            </div>
          ) : (
            <table className="search-results-table" onContextMenu={ctxMenu.onTableContextMenu}>
              <thead>
                <tr>
                  <th className="no-sort">Registration</th>
                  <th>Type</th>
                  <th>Config</th>
                  <th>Gross Wt</th>
                  <th>Payload</th>
                  <th>Compartments</th>
                  <th>Rear Lift</th>
                  <th>Default Driver</th>
                  <th>Default Haulier</th>
                  <th className="status-col">Status</th>
                  <th className="actions-col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredVehicles.map(vehicle => {
                  return (
                    <tr
                      key={vehicle.id}
                      className={vehicle.status !== 'ACTIVE' ? 'vm-archived-row' : ''}
                      onContextMenu={ctxMenu.onRowContextMenu(vehicle)}
                    >
                      <td className="vm-reg-cell">
                        <span className="vm-reg-display" onClick={() => handleEdit(vehicle)} title="Click to edit vehicle">
                          <strong>{vehicle.registration}</strong>
                        </span>
                      </td>
                      <td>{fmtType(vehicle.vehicle_type)}{vehicle.has_moffett ? ' + Moffett' : ''}</td>
                      <td>{vehicle.axle_configuration || '—'}</td>
                      <td>{fmtWeight(vehicle.gross_weight)}</td>
                      <td>{fmtWeight(vehicle.payload)}</td>
                      <td title={vehicle.has_moveable_doors
                        ? 'Moveable-door body — compartment tonnages set per job in transport planning'
                        : (Array.isArray(vehicle.compartments) && vehicle.compartments.length > 0
                          ? vehicle.compartments.map(c => (c?.capacity != null && c.capacity !== '' ? `${formatQuantity(c.capacity)}T` : '–')).join(' · ')
                          : '')}>
                        {renderCompartments(vehicle)}
                      </td>
                      <td>
                        {vehicle.rear_lift
                          ? <span className="material-symbols-rounded" style={{ color: 'var(--color-success)', fontSize: 18 }} title="Rear lift fitted">check_circle</span>
                          : '—'}
                      </td>
                      <td>{vehicle.default_driver_name || '—'}</td>
                      <td>{vehicle.default_haulier_name || '—'}</td>
                      <td className="status-col">
                        <span className={`status-badge ${vehicle.status === 'ACTIVE' ? 'status-active' : 'status-archived'}`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="actions-col">
                        <RowActionsMenu actions={buildRowActions(vehicle)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <VehicleModal
        isOpen={modalOpen}
        vehicle={editingVehicle}
        onClose={() => { setModalOpen(false); setEditingVehicle(null); }}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => { if (confirmDialog.onConfirm) confirmDialog.onConfirm(); }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
      />

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add vehicle', onClick: handleAdd },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: fetchVehicles },
          {
            key: 'show-archived',
            icon: 'inventory_2',
            label: 'Show archived',
            active: showArchived,
            onClick: () => setShowArchived(v => !v)
          },
          {
            key: 'clear-filters',
            icon: 'filter_alt_off',
            label: 'Clear filters',
            onClick: () => { setSearchTerm(''); setShowArchived(false); }
          }
        ]}
      />
    </div>
  );
}

export default VehiclesManager;
